
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useInvestorProcessing } from "@/hooks/investor";
import { useEmailManagement } from "@/hooks/useEmailManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import DataTabs from "@/components/tabs/DataTabs";
import EmailGeneratorSection from "@/components/emails/EmailGeneratorSection";
import EmailDrafts from "./EmailDrafts";
import SentEmails from "./SentEmails";

const EmailWorkspace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "generator");
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  
  console.log("EmailWorkspace: Component mounted, starting data load");
  
  const {
    investors,
    contacts,
    businessInfo,
    selectedInvestor,
    isEnriching,
    processedIds,
    setSelectedInvestor,
    setIsEnriching,
    loadSavedData,
    handleInvestorUpdate,
    handleProcessInvestor,
    handleEnrichInvestor,
    isLoadingData
  } = useInvestorProcessing();

  const {
    emails,
    emailStats,
    isLoading: isEmailLoading,
    setIsLoading,
    loadEmailData,
    handleEmailSent,
    handleProviderUpdated
  } = useEmailManagement();

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Update tab when URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["generator", "drafts", "sent"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Load data on initial mount with error handling
  useEffect(() => {
    const loadData = async () => {
      if (!isInitialDataLoading) return;
      
      try {
        console.log("EmailWorkspace: Starting initial data load");
        setDataLoadError(null);
        
        // Load data in parallel with timeout
        const dataPromises = [
          loadSavedData(),
          loadEmailData()
        ];
        
        await Promise.all(dataPromises);
        console.log("EmailWorkspace: Initial data load completed successfully");
        
      } catch (error) {
        console.error("EmailWorkspace: Error loading initial data:", error);
        setDataLoadError("Failed to load data. Please try refreshing the page.");
      } finally {
        setIsInitialDataLoading(false);
        console.log("EmailWorkspace: Initial data loading finished");
      }
    };
    
    loadData();
  }, [loadSavedData, loadEmailData, isInitialDataLoading]);

  const handleEmailSentWrapper = () => {
    console.log("EmailWorkspace: Email sent wrapper called");
  };

  // Show loading state only for initial data load
  if (isInitialDataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-sm text-gray-600">Loading email workspace...</p>
        </div>
      </div>
    );
  }

  // Show error state if data loading failed
  if (dataLoadError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <p className="text-sm text-red-600">{dataLoadError}</p>
          <button 
            onClick={() => {
              setDataLoadError(null);
              setIsInitialDataLoading(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Email Workspace</h1>
        <p className="text-muted-foreground">
          Generate personalized emails, manage drafts, and track your investor outreach in one place.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generator">Email Generator</TabsTrigger>
          <TabsTrigger value="drafts">Drafts ({emails.filter(e => e.status === 'draft').length})</TabsTrigger>
          <TabsTrigger value="sent">Sent ({emailStats.totalSent})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="generator" className="space-y-6">
          {investors.length > 0 ? (
            <>
              <DataTabs
                investors={investors}
                contacts={contacts}
                emails={emails}
                selectedInvestor={selectedInvestor}
                selectedEmail={null}
                relevanceContext=""
                isEnriching={isEnriching}
                onSelectInvestor={setSelectedInvestor}
                onInvestorUpdate={handleInvestorUpdate}
                onProcessInvestor={handleProcessInvestor}
                onEnrichInvestor={handleEnrichInvestor}
                onViewEmail={() => {}}
                onCloseEmailDetails={() => {}}
                onEmailUpdated={() => {}}
                onProviderUpdated={handleProviderUpdated}
                setIsEnriching={setIsEnriching}
              />

              {selectedInvestor && (
                <EmailGeneratorSection
                  investor={selectedInvestor}
                  contacts={contacts}
                  businessInfo={businessInfo}
                  isLoading={isEmailLoading}
                  setIsLoading={setIsLoading}
                  emails={emails}
                  onEmailSent={handleEmailSentWrapper}
                />
              )}
            </>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>No Investor Data</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Upload your investors data in the Data Workspace to start generating emails.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="drafts" className="space-y-6">
          <EmailDrafts />
        </TabsContent>
        
        <TabsContent value="sent" className="space-y-6">
          <SentEmails />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailWorkspace;
